import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, KeyRound, ShieldCheck, Wallet } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function CreatorSettings() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const changePw = async () => {
    if (form.new_password !== form.confirm) { toast.error("New passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/vid/creator/change-password", form);
      toast.success("Password changed successfully");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8 max-w-2xl" data-testid="creator-settings-page">
      <PageHeader title="Settings" subtitle="Account security & payout preferences" testid="settings-title" />

      <div className="bg-card border border-border rounded-2xl p-6 space-y-3" data-testid="settings-account">
        <p className="font-display font-bold text-sm flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Account</p>
        <div className="text-sm text-muted-foreground space-y-1">
          <p>Email: <span className="font-semibold text-foreground">{user?.email}</span></p>
          <p>Account type: <span className="font-semibold text-foreground">Creator — Free</span></p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid="settings-password">
        <p className="font-display font-bold text-sm flex items-center gap-2"><KeyRound className="h-4 w-4" /> Change Password</p>
        <div className="space-y-1.5">
          <Label>Current Password</Label>
          <Input data-testid="cs-current" type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="h-11 rounded-xl" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>New Password (min 6 chars)</Label>
            <Input data-testid="cs-new" type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="h-11 rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Confirm New Password</Label>
            <Input data-testid="cs-confirm" type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="h-11 rounded-xl" />
          </div>
        </div>
        <Button data-testid="cs-save" onClick={changePw} disabled={busy || !form.current_password || !form.new_password} className="rounded-full font-bold gradient-bg text-white border-0 px-8">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
        </Button>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 flex flex-wrap items-center justify-between gap-3" data-testid="settings-payout">
        <div>
          <p className="font-display font-bold text-sm flex items-center gap-2"><Wallet className="h-4 w-4" /> Payout Details</p>
          <p className="text-xs text-muted-foreground mt-0.5">UPI / bank details used for withdrawals are managed on the Earnings page.</p>
        </div>
        <Link to="/creator/earnings"><Button variant="outline" data-testid="settings-goto-payout" className="rounded-full font-bold">Manage Payout</Button></Link>
      </div>
    </div>
  );
}
